import { Capacitor } from '@capacitor/core'
import { useAuth, useAxios } from '@healthblocks-io/core'
import { useSetRecoilState } from 'recoil'

import { localUspSlidesAtom } from '@/modules/app/pages/usp/UspSlides.page'
import { useClearLocalSession } from '@/modules/auth/hooks/useClearLocalSession'
import { useSessionCredentials } from '@/modules/auth/hooks/useSessionCredentials'
import { useBiometrics } from '@/modules/biometrics/hooks/useBiometrics'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { useOAuth2 } from '@/modules/healthblocks/hooks/useOAuth2'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { openUrl } from '@/utils/external-resources'
import { StorageKeys, StorageService } from '@/utils/storageService'

function useLogout() {
  const { signOut } = useAuth()
  const axios = useAxios()
  const clearLocalSession = useClearLocalSession()
  const { setLockout } = useLockoutTime()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  const { checkBiometricState } = useBiometrics()
  const { biometricPermission } = useBiometricsPermission()

  const { redirectUrl } = useOAuth2()
  const { clearCredentials } = useSessionCredentials()
  const setLocalUspSlidesAtom = useSetRecoilState(localUspSlidesAtom)

  /**
   * This function is used to logout the user.
   * If the user is logged in for a mandatee, it will call the logout function.
   * If biometrics are enrolled, it will set the lockout state to true (meaning we will show the lock screen).
   * If biometrics are not enrolled or if we run the app in the browser, it will call the logout function.
   */
  async function softLogout() {
    if (!Capacitor.isNativePlatform() || isLoggedInForMandatee) {
      await logout()
      return
    }

    checkBiometricState().then(async (state) => {
      if (state?.isAvailable && biometricPermission === BiometricsAppPermission.GRANTED) {
        setLockout(true)

        return
      }

      await logout()
    })
  }

  /**
   * This function is used to logout the user.
   * It will clear the filtering, remove the last logged in date, remove the notification channel, reset the lockout, and call the logout function.
   * If the logout is successful, it navigates to the IdP logout URL. On web this happens in the same tab,
   * so the user has to pass through the eHealth logout page and is redirected back to the login screen
   * afterwards. On native `target` is ignored: the URL opens in the system browser, falling back to the
   * in-app browser, and the local sign-out below waits for that browser to be presented.
   * If the logout fails, it will log the error.
   * Finally, it will call the signOut function and reset the session.
   */
  async function logout() {
    // Remove last logged in date
    await StorageService.removeItem(StorageKeys.LastLoggedInAt)

    // Clear cached responses, notification channel, task count, badge and Mixpanel identification
    await clearLocalSession()

    // Reset the USP slides flag so a new patient onboarding on this device
    // sees the intro slides again (the flag is device-scoped, not user-scoped)
    setLocalUspSlidesAtom(false)

    try {
      // Reset lockout
      setLockout(false)

      const ok = await axios.post('/authenticator/logout', {}, { params: { post_logout_redirect_uri: redirectUrl } })

      if (ok.data.url) await openUrl(ok.data.url, { target: '_self' })
    } catch (error) {
      console.info('logout', error)
    } finally {
      await clearCredentials()
      signOut()
    }
  }

  return {
    softLogout,
    logout,
  }
}

export { useLogout }
